import type { IMemoryId, ISSEResponse } from '@/service/agent';
import type { IInstructionTemplate } from '@/service/friend';
import type { CommonResponse, EmptyResponse } from '@/types/responseModel';
import { Request } from '@/utils/request';

export enum ECallStage {
  INCOMING = 'incoming',
  CONNECTING = 'connecting',
  AI_SPEAKING = 'ai_speaking',
  USER_SPEAKING = 'user_speaking',
  THINKING = 'thinking',
  IDLE = 'idle'
}

export interface ICallAuthor {
  avatar: string;
  cover: string;
  id: string;
  route: string;
  username: string;
  video: string;
}

export enum ECallStatus {
  DOING = 'doing',
  DONE = 'done',
  UNDONE = 'undone'
}

export interface ICallDetail {
  templateId?: string;
  userInput?: string;
  opening?: string;
  memoryIds?: IMemoryId[];
  title?: string;
  systemPrompt?: string;
  timeLimit?: number;
  status?: ECallStatus;
  author: ICallAuthor;
}

export function getCallDetail(params: { templateId: string }) {
  return Request<CommonResponse<ICallDetail>>({
    url: '/rest/os/call/template/detail',
    method: 'get',
    params
  });
}

export interface IInitCallRoom {
  appId: string;
  roomId: string;
  userId: string;
  token: string;
  taskId: string;
  agentUserId: string;
  sessionId?: string;
  opening?: string;
}

export interface RTCConfig {
  appId: string;
  roomId: string;
  userId: string;
  token: string;
}

export interface VoiceChatConfig {
  AppId: string;
  RoomId: string;
  TaskId: string;
  AgentConfig: {
    UserId: string;
    WelcomeMessage?: string;
    TargetUserId: string[];
    EnableConversationStateCallback?: boolean;
  };
}

export function initCallRoom(data: { templateId: string }) {
  return Request<CommonResponse<IInitCallRoom>>({
    url: '/rest/os/call/get/init/param',
    method: 'post',
    data
  });
}

export function startCall(data: { taskId: string }) {
  return Request<EmptyResponse>({
    url: '/rest/os/call/start/voice',
    method: 'post',
    data
  });
}

export function stopCall(data: { taskId: string }) {
  return Request<EmptyResponse>({
    url: '/rest/os/call/stop',
    method: 'post',
    data
  });
}

export function createCall(data: {
  userInput?: string;
  memoryIds?: IMemoryId[];
  timeLimit?: number;
  templateId?: string;
  regenerate?: boolean;
  title?: string;
  opening?: string;
  systemPrompt?: string;
}) {
  return Request<CommonResponse<{ templateId: string }>>({
    url: '/rest/os/call/template/create',
    method: 'post',
    data
  });
}

export function getCallTemplateRecommend(lang?: string) {
  return Request<CommonResponse<{ instructionTemplate: IInstructionTemplate[] }>>({
    url: '/rest/os/call/template/recommend',
    method: 'get',
    params: lang ? { lang } : undefined
  });
}

export interface ICallTemplateMemoryItem {
  id: string;
  type: string;
}

export type ICallTemplateMemoryRecall = ISSEResponse<IMemoryId[] | string>;

export type ICallTemplateOpening = ISSEResponse<{
  systemPrompt?: string;
  opening?: string;
}>;

export type ICallTemplateComplete = ISSEResponse<{
  cover?: string;
  route?: string;
  id?: string;
  avatar?: string;
  video?: string;
  username?: string;
  title?: string;
}>;

export interface ICallMessage {
  messageId: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp?: number;
  url?: string;
  roundId?: number;
  definite?: boolean;
}

export function saveChatMessages(data: { sessionId: string; messages: ICallMessage[] }) {
  return Request<EmptyResponse>({
    url: '/rest/os/call/messages/save',
    method: 'post',
    data
  });
}

export interface ICallStats {
  callCount: number;
  totalDurationSaved: number;
}

export interface ICallRecordCaller {
  userId?: string;
  username?: string;
  avatar?: string;
  isAnonymous?: boolean;
}

export interface ICallRecordItem {
  sessionId?: string;
  caller?: ICallRecordCaller;
  summary?: string;
  duration?: number;
  callTime?: string;
}

export interface ICallRecordList {
  total?: number;
  list?: ICallRecordItem[];
}

export interface ICallRecord {
  template: ICallDetail;
  stats: ICallStats;
  records: ICallRecordList;
}

export function getCallRecords(params: { templateId: string; pageNo: number; pageSize: number }) {
  return Request<CommonResponse<ICallRecord>>({
    url: '/rest/os/call/template/records',
    method: 'get',
    params: {
      ...params,
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 999
    }
  });
}

export interface ICallMessageItem {
  messageId?: string;
  role?: string;
  content?: string;
  timestamp?: number;
  audioUrl?: string;
}

export interface ICallMessageDetail {
  sessionId?: string;
  summary?: string;
  duration?: number;
  startTime?: string;
  caller?: ICallRecordCaller;
  assistant?: ICallRecordCaller;
  messages?: ICallMessageItem[];
}

export function getCallMessages(params: { sessionId: string }) {
  return Request<CommonResponse<ICallMessageDetail>>({
    url: '/rest/os/call/message/detail',
    method: 'get',
    params
  });
}

export function deleteCallTemplate(data: { templateId: string }) {
  return Request<EmptyResponse>({
    url: '/rest/os/call/template/delete',
    method: 'post',
    data
  });
}
